import { BookingAuthApi } from '@agendor/shared';
import { initClient } from '@ts-rest/core';
import { BaseAuthService, SessionExpiredError, delay } from '@agendor/ui';

class AuthApiError extends Error {
    constructor(readonly status: number) {
        super(`Auth API error: ${status}`);
    }
}

export { SessionExpiredError };

export default class AuthService extends BaseAuthService {
    private _businessSlug: string;
    private _authClient;

    constructor(params: { serverRootPath: string; businessSlug: string; initialAccessToken: string | null }) {
        super({ serverRootPath: params.serverRootPath, initialAccessToken: params.initialAccessToken });
        this._businessSlug = params.businessSlug;

        this._authClient = initClient(BookingAuthApi.contract, {
            baseUrl: `${params.serverRootPath}/booking`,
            api: async (args) => {
                const response = await fetch(args.path, {
                    method: args.method,
                    headers: args.headers as Record<string, string>,
                    body: args.body,
                    credentials: 'include',
                });

                if (!response.ok) throw new AuthApiError(response.status);

                const body = response.status === 204 ? undefined : await response.json().catch(() => undefined);

                return { status: response.status, body, headers: response.headers };
            },
        });
    }

    async refreshAccessToken(): Promise<boolean> {
        return this._refreshAccessToken();
    }

    protected async _refreshAccessToken(): Promise<boolean> {
        try {
            const doRefresh = () =>
                this._authClient.refresh({
                    query: { slug: this._businessSlug },
                });

            const result = await retryOn409(doRefresh);
            this.setAccessToken(result.body.accessToken);
            return true;
        } catch {
            this.clearAccessToken();
            return false;
        }
    }

    async logout(): Promise<void> {
        return this._logout();
    }

    protected async _logout(): Promise<void> {
        try {
            await this._authClient.logout({});
        } finally {
            this.clearAccessToken();
        }
    }
}

async function retryOn409<T>(fn: () => Promise<T>): Promise<T> {
    try {
        return await fn();
    } catch (error) {
        if (error instanceof AuthApiError && error.status === 409) {
            await delay(500);
            return await fn();
        }
        throw error;
    }
}

/**
 * Wraps a fetch call with automatic retry on 409 (token rotation race condition).
 * Waits 500ms before retrying once.
 */
export async function fetchWithRetryOn409(doFetch: () => Promise<Response>): Promise<Response> {
    let response = await doFetch();
    if (response.status === 409) {
        await delay(500);
        response = await doFetch();
    }
    return response;
}
